(function () {
  const selectors = {
    config: '[data-virtual-bundle-single-product]',
    addButton: '[data-product-summary-add-to-cart]',
    variantInput: 'input[name="id"]',
    quantityInput: 'input[name="quantity"]',
    optionSelect: '[data-product-summary-option-select]',
    optionSwatch: '[data-product-summary-option-swatch]',
    addLabel: '[data-product-summary-add-label]',
    addIcon: '[data-product-summary-add-icon]',
    miniCartPanel: '#mini-cart',
    miniCartTrigger: '[data-panel-trigger="mini-cart"]'
  };
  const observedForms = new WeakSet();

  function parseConfig(form) {
    const configEl = form.querySelector(selectors.config);
    if (!configEl) return null;

    try {
      const config = JSON.parse(configEl.textContent || '{}');
      const hasSingleProductVariants = Array.isArray(config.singleProductVariants);
      const hasMappedBundleProducts = Array.isArray(config.mappedBundleProducts);
      if (!config.isVirtualBundle || (!hasSingleProductVariants && !hasMappedBundleProducts)) return null;
      return config;
    } catch (error) {
      console.error('[virtual-bundle] Invalid config JSON', error);
      return null;
    }
  }

  function normalizeOptions(options) {
    return Array.isArray(options) ? options.map((option) => String(option || '').trim()) : [];
  }

  function optionsMatch(left, right) {
    const leftOptions = normalizeOptions(left);
    const rightOptions = normalizeOptions(right);
    return leftOptions.length === rightOptions.length && leftOptions.every((option, index) => option === rightOptions[index]);
  }

  function findSelectedBundleVariant(form, config) {
    const variantInput = form.querySelector(selectors.variantInput);
    const selectedId = Number.parseInt(variantInput ? variantInput.value : '', 10);
    return config.bundleVariants.find((variant) => Number(variant.id) === selectedId) || config.bundleVariants[0] || null;
  }

  function findSingleVariant(bundleVariant, config) {
    if (!bundleVariant) return null;

    const exactMatch = config.singleProductVariants.find((variant) => optionsMatch(variant.options, bundleVariant.options));
    if (exactMatch) return exactMatch;

    const bundleOptions = normalizeOptions(bundleVariant.options);
    return config.singleProductVariants.find((variant) => normalizeOptions(variant.options)[0] === bundleOptions[0]) || null;
  }

  function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
  }

  function extractPackNumber(value) {
    const match = normalizeText(value).match(/\b(\d+)\s*(?:pack|packs|tray|trays|pot|pots)?\b/);
    return match ? Number.parseInt(match[1], 10) : null;
  }

  function variantPackNumbers(variant) {
    return normalizeOptions(variant?.options)
      .concat([variant?.title])
      .map(extractPackNumber)
      .filter((number) => Number.isFinite(number));
  }

  function findMappedBundleProduct(config) {
    const products = Array.isArray(config.mappedBundleProducts) ? config.mappedBundleProducts : [];
    if (products.length === 0) return null;

    const preferredProductId = Number(config.preferredProductId);
    if (Number.isFinite(preferredProductId) && preferredProductId > 0) {
      const preferred = products.find((product) => Number(product.id) === preferredProductId);
      if (preferred) return preferred;
    }

    if (config.bundleProductHandle === 'epic-16-cell-seed-starting-trays') {
      const nonFourPack = products.find((product) => {
        const text = `${normalizeText(product.title)} ${normalizeText(product.handle)}`;
        return !text.includes('4-pack') && !text.includes('4 pack');
      });
      if (nonFourPack) return nonFourPack;
    }

    return products.find((product) => product.variants?.some((variant) => variant.available)) || products[0] || null;
  }

  function findMappedBundleVariant(bundleVariant, config) {
    const mappedProduct = findMappedBundleProduct(config);
    const variants = Array.isArray(mappedProduct?.variants) ? mappedProduct.variants : [];
    if (!bundleVariant || variants.length === 0) return null;

    const exactMatch = variants.find((variant) => optionsMatch(variant.options, bundleVariant.options));
    if (exactMatch) return exactMatch;

    const selectedPackNumbers = variantPackNumbers(bundleVariant);
    if (selectedPackNumbers.length > 0) {
      const packMatch = variants.find((variant) => {
        const candidatePackNumbers = variantPackNumbers(variant);
        return candidatePackNumbers.some((number) => selectedPackNumbers.includes(number));
      });
      if (packMatch) return packMatch;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function findSourceVariant(form, config) {
    const bundleVariant = findSelectedBundleVariant(form, config);
    if (config.mode === 'mappedBundle') return findMappedBundleVariant(bundleVariant, config);
    return findSingleVariant(bundleVariant, config);
  }

  function isHiddenWhenOutOfStock(variant) {
    return !!(variant && !variant.available && variant.hideWhenOos);
  }

  function shouldHideOptionValue(variants, position, value) {
    const matchingVariants = variants.filter((variant) => normalizeOptions(variant.options)[position] === value);
    return matchingVariants.length > 0 && matchingVariants.every(isHiddenWhenOutOfStock);
  }

  function updateHiddenOptionValues(form) {
    const config = parseConfig(form);
    if (!config || config.mode !== 'singleProduct') return;

    const sourceVariants = Array.isArray(config.singleProductVariants) ? config.singleProductVariants : [];
    if (sourceVariants.length === 0) return;

    const hiddenValuesByPosition = new Map();
    const selects = Array.from(form.querySelectorAll(selectors.optionSelect));

    selects.forEach((select, selectIndex) => {
      const position = Number.parseInt(select.dataset.productSummaryOptionPosition || '', 10);
      const optionPosition = Number.isFinite(position) ? position : selectIndex;
      const hiddenValues = new Set();

      Array.from(select.options).forEach((option) => {
        const isHidden = shouldHideOptionValue(sourceVariants, optionPosition, option.value);
        option.hidden = isHidden;
        if (isHidden) hiddenValues.add(option.value);
      });

      hiddenValuesByPosition.set(optionPosition, hiddenValues);

      if (select.selectedOptions[0]?.hidden) {
        const firstVisibleOption = Array.from(select.options).find((option) => !option.hidden);
        if (firstVisibleOption) {
          select.value = firstVisibleOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    form.querySelectorAll(selectors.optionSwatch).forEach((swatch) => {
      const position = Number.parseInt(swatch.dataset.productSummaryOptionPosition || '', 10);
      const hiddenValues = hiddenValuesByPosition.get(position);
      const isHidden = !!hiddenValues?.has(swatch.value);
      const item = swatch.closest('li');
      if (item) item.hidden = isHidden;
    });
  }

  function getCartQuantity(sourceVariant, config, quantity) {
    if (config.mode === 'mappedBundle') return quantity;
    return quantity * (Number.parseInt(config.bundleQuantity, 10) || 4);
  }

  function getQuantity(form) {
    const quantityInput = form.querySelector(selectors.quantityInput);
    const quantity = Number.parseInt(quantityInput ? quantityInput.value : '', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function setButtonLoading(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.toggleAttribute('aria-busy', isLoading);
    button.classList.toggle('button-disabled', isLoading);
  }

  function setTextContent(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }

  function setHidden(element, isHidden) {
    if (element && element.hidden !== isHidden) element.hidden = isHidden;
  }

  function setNotifyTrigger(button, shouldNotify) {
    if (!button) return;

    if (shouldNotify) {
      if (!button.hasAttribute('data-omnisend-notify-trigger')) {
        button.setAttribute('data-omnisend-notify-trigger', '');
      }
      button.removeAttribute('name');
      return;
    }

    button.removeAttribute('data-omnisend-notify-trigger');
    button.setAttribute('name', 'add');
  }

  function setButtonAvailability(form) {
    const config = parseConfig(form);
    if (!config) return;

    const button = form.querySelector(selectors.addButton);
    if (!button || button.hasAttribute('data-virtual-bundle-loading')) return;

    const sourceVariant = findSourceVariant(form, config);
    const isAvailable = !!(sourceVariant && sourceVariant.available);
    const label = button.querySelector(selectors.addLabel);
    const icon = button.querySelector(selectors.addIcon);
    const defaultLabel = button.getAttribute('data-product-summary-add-label-default') || 'Add to Cart';
    const soldOutLabel = button.getAttribute('data-product-summary-add-label-soldout') || 'Sold out';

    form.dataset.virtualBundleAvailable = String(isAvailable);
    if (button.disabled === isAvailable) button.disabled = !isAvailable;
    button.classList.toggle('button-disabled', !isAvailable);
    const buttonType = isAvailable ? 'submit' : 'button';
    if (button.type !== buttonType) button.type = buttonType;
    setNotifyTrigger(button, !isAvailable);
    setTextContent(label, isAvailable ? defaultLabel : soldOutLabel);
    setHidden(icon, !isAvailable);
  }

  function observeVirtualBundleForm(form) {
    if (!form || observedForms.has(form) || !parseConfig(form)) return;
    observedForms.add(form);

    const observer = new MutationObserver(() => scheduleButtonAvailability(form));
    observer.observe(form, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'type', 'data-omnisend-notify-trigger'],
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  function setAllButtonAvailability(root = document) {
    root.querySelectorAll('form').forEach((form) => {
      observeVirtualBundleForm(form);
      updateHiddenOptionValues(form);
      setButtonAvailability(form);
    });
  }

  function scheduleButtonAvailability(root = document) {
    window.requestAnimationFrame(() => {
      setAllButtonAvailability(root);
      window.setTimeout(() => setAllButtonAvailability(root), 0);
    });
  }

  function observeNewVirtualBundleForms() {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      const hasVirtualBundleNode = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches(selectors.config) || !!node.querySelector(selectors.config);
        });
      });
      if (!hasVirtualBundleNode) return;
      scheduleButtonAvailability();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  async function addSourceVariantToCart(sourceVariant, config, quantity) {
    const addUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(sourceVariant.id),
            quantity: getCartQuantity(sourceVariant, config, quantity)
          }
        ]
      })
    });

    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  async function refreshCartState() {
    const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const cart = await response.json();
    window.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart, source: 'virtual-bundle-single-product' } }));
    return cart;
  }

  function openMiniCart() {
    const trigger = document.querySelector(selectors.miniCartTrigger);
    if (trigger) {
      trigger.click();
      return;
    }

    if (!document.querySelector(selectors.miniCartPanel)) {
      window.location.href = '/cart';
    }
  }

  function closeChooseOptionsPanel(form) {
    const panel = form.closest('[data-panel]');
    const closeButton = panel ? panel.querySelector('.panel-close') : null;
    if (closeButton) closeButton.click();
  }

  function getErrorMessage(error) {
    if (error && typeof error.description === 'string') return error.description;
    if (error && typeof error.message === 'string') return error.message;
    return "Couldn't add to cart. Please try again.";
  }

  document.addEventListener(
    'submit',
    async function (event) {
      const form = event.target.closest('form');
      if (!form) return;

      const config = parseConfig(form);
      if (!config) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const button = event.submitter && event.submitter.closest(selectors.addButton)
        ? event.submitter.closest(selectors.addButton)
        : form.querySelector(selectors.addButton);
      const sourceVariant = findSourceVariant(form, config);

      if (!sourceVariant || !sourceVariant.available) {
        window.alert('This option is sold out.');
        setButtonAvailability(form);
        return;
      }

      if (button) button.setAttribute('data-virtual-bundle-loading', 'true');
      setButtonLoading(button, true);

      try {
        const added = await addSourceVariantToCart(sourceVariant, config, getQuantity(form));
        window.dispatchEvent(new CustomEvent('cart:item-added', { detail: { response: added, source: 'virtual-bundle-single-product' } }));
        await refreshCartState();
        closeChooseOptionsPanel(form);
        openMiniCart();
      } catch (error) {
        window.alert(getErrorMessage(error));
      } finally {
        if (button) button.removeAttribute('data-virtual-bundle-loading');
        setButtonLoading(button, false);
        setButtonAvailability(form);
      }
    },
    true
  );

  document.addEventListener('change', function (event) {
    const form = event.target.closest('form');
    if (form) scheduleButtonAvailability(form);
  });

  window.addEventListener('product:variant-changed', function () {
    scheduleButtonAvailability();
  });

  window.addEventListener('load', function () {
    scheduleButtonAvailability();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observeNewVirtualBundleForms();
      scheduleButtonAvailability();
    });
  } else {
    observeNewVirtualBundleForms();
    scheduleButtonAvailability();
  }
})();
